import { Fragment, ReactNode, useState } from 'react'

import clsx from 'clsx'
import { useKeenSlider } from 'keen-slider/react'

import { isDevelopment } from '@/utils/isDevelopment'

import { Warning } from '../Warning'

type Tab = {
	tab?: ReactNode
	content: ReactNode
}

type Props = {
	className?: string
	tabs: Tab[]
}

export function TabsCarousel({ className, tabs }: Props) {
	const [activeIndex, setActiveIndex] = useState(0)

	const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
		loop: true,
		slides: { perView: 1, origin: 'auto' },
		selector: ':scope > div',
		slideChanged(e) {
			if (!setActiveIndex) return
			setActiveIndex(e.track.details.rel)
		}
	})
	if (tabs?.length === 0) {
		if (isDevelopment()) {
			return <Warning className={className}>There are no tabs</Warning>
		}

		return null
	}

	return (
		<div className={className}>
			<div className="hidden lg:flex border-b border-gray-900">
				{tabs.map((tab, i) => (
					<button
						className={clsx(
							'slider-control',
							'flex-1 pb-7.5 border-b-[5px] transition duration-200 hover:opacity-100',
							activeIndex === i ? 'border-bc-blue opacity-100' : 'border-transparent opacity-40'
						)}
						onClick={() => instanceRef.current?.moveToIdx(i)}
						key={i}
					>
						{tab.tab}
					</button>
				))}
			</div>

			<div
				className="relative flex touch-pan-y select-none overflow-hidden focus:outline-0"
				ref={sliderRef}
			>
				{tabs.map((tab, i) => (
					<Fragment key={i}>{tab.content}</Fragment>
				))}
			</div>

			<div className="flex justify-center gap-x-2.5 lg:hidden mt-14 md:mt-20">
				{tabs.map((tab, i) => (
					<button
						className="relative flex items-center justify-center w-5 h-5"
						onClick={() => instanceRef.current?.moveToIdx(i)}
						key={i}
					>
						<span className="sr-only">Go to slide {i + 1}</span>
						<div
							aria-hidden="true"
							className={clsx(
								'w-1.5 h-1.5 absolute rounded-full bg-bc-black',
								activeIndex === i ? 'opacity-100' : 'opacity-25'
							)}
						/>
					</button>
				))}
			</div>
		</div>
	)
}
